import React, { useCallback, useEffect, memo } from 'react';
import { compose } from 'redux';
import { useHistory } from 'react-router';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { isEmpty, size } from 'lodash';
import scaleSize from 'utils/scale-size';
import { selectPageData, selectPageIsLoading } from 'store/pages/selectors';
import { CONTENT_TYPES } from 'constants/content-types';
import { loadData } from 'store/pages/actions';
import { appActions } from 'store/app';
import { playerActions } from 'store/player';
import { LIST_ITEMS_SIZE } from 'config';
import WithMouseControls from 'components/HOCs/WithMouseControls';
import ScrollTopHack from 'components/ScrollTopHack';
import CustomCollection from 'components/CustomCollection';
import ListItem from 'components/CustomCollection/ListItem';
import { FAVORITES } from 'store/pages/loaders/my-watchlist';
import { StyledSectionPage } from './StyledComponents';
import TabContent from './TabContent';
import { StyledGrid } from './StyledComponents';

const ROW_SIZE = 4;

const SectionPage = ({ currentMenuItem, isHomePage }) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { id: pageId } = currentMenuItem;
  const pageData = useSelector((state) => selectPageData(state, { pageId }));
  const isLoading = useSelector((state) => selectPageIsLoading(state, { pageId }));

  const onTabEnter = useCallback(
    ({ id }) => {
      const currentTab = pageData.tabs.find((tab) => tab.id === id);
      if (isEmpty(currentTab.content) || currentTab.id === FAVORITES) {
        const params = { extId: currentTab.ext_id, tabId: id };
        dispatch(loadData({ pageId, params }));
      }
    },
    [dispatch, pageData, pageId],
  );

  const onContentEnter = useCallback(
    (item) => {
      const itemType = item.asset_type || item.type;
      const itemID = item.asset_id || item.id;
      switch (itemType) {
        case 'movie':
        case 'series':
          // todo: case 'series':
          history.push(`/details/${itemType}/${itemID}`);
          break;
        case 'channel':
          dispatch(playerActions.navigateToPlayer({ item }));
          break;
        default:
          // banner has no type
          dispatch(appActions.resolveBannerNavigation(item));
      }
    },
    [dispatch, history],
  );

  const loadExtraItems = useCallback(
    (tab) => {
      const { content } = tab;
      if (content.isOnLazyLoad || content.isAllItemsLoaded) return;
      const params = { extId: tab.ext_id, tabId: tab.id, offset: size(tab.content.data) };
      dispatch(loadData({ pageId, params, loader: false }));
    },
    [dispatch, pageId],
  );

  const onGridFocus = (item, index) => {
    const rows = pageData.content?.length / ROW_SIZE;
    const currentRowIndex = Math.ceil(index / ROW_SIZE);
    const isRequiresExtraLoad = currentRowIndex >= rows - 1 - 1;
    const params = { offset: size(pageData.content) };
    if (isRequiresExtraLoad && !pageData.isOnLazyLoad && !pageData.isAllItemsLoaded) {
      dispatch(loadData({ pageId, params, loader: false }));
    }
  };

  let content = null;

  useEffect(() => {
    if (!pageData) dispatch(loadData({ pageId: currentMenuItem.id }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!pageData) return null;

  if (!isEmpty(pageData.tabs)) {
    content = (
      <TabContent
        title={pageData.name}
        tabs={pageData.tabs}
        collections={[]}
        onTabEnter={onTabEnter}
        onContentEnter={onContentEnter}
        loadExtraItems={loadExtraItems}
      />
    );
  } else if (pageData.contentType === CONTENT_TYPES.TV_SERIES) {
    content = (
      <StyledGrid
        title="TV Channels"
        items={pageData.content || []}
        rowSize={ROW_SIZE}
        gridId={`grid-section-0`}
        key={`grid-section-0`}
        onEnter={onContentEnter}
        onFocus={onGridFocus}
        component={ListItem.TV_SERIES}
        itemSize={LIST_ITEMS_SIZE.SMALL}
      />
    );
  } else {
    content = (
      <CustomCollection
        collections={pageData}
        isLoading={isLoading}
        onContentEnter={onContentEnter}
        showClock={isHomePage}
        showLogo={isHomePage}
        top={scaleSize(140)}
        left={scaleSize(220)}
        startListScrollIndex={1}
      />
    );
  }

  return <StyledSectionPage>{content}</StyledSectionPage>;
};

SectionPage.propTypes = {
  currentMenuItem: PropTypes.shape({
    id: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    path: PropTypes.string.isRequired,
  }).isRequired,
  isHomePage: PropTypes.bool,
};

export default compose(memo, WithMouseControls, ScrollTopHack)(SectionPage);
